import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { getProjects, getTasks, getUsers, getNotes } from '../api';
import api from '../api';
import ResearchCenter from '../components/ResearchCenter';
import ProjectActivityTimeline from '../components/ProjectActivityTimeline';
import DeletionLog from '../components/DeletionLog';

export default function ReportsPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const [reportTab, setReportTab] = useState('overview');
  const [activityProjectId, setActivityProjectId] = useState('');
  const [activityProjects, setActivityProjects] = useState([]);
  const [loadingActivityProjects, setLoadingActivityProjects] = useState(true);

  useEffect(() => {
    api.get('/gpf/v1/activity-log/projects')
      .then(res => setActivityProjects(res.data || []))
      .catch(console.error)
      .finally(() => setLoadingActivityProjects(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const pid = params.get('project_id');
    if (tab === 'activity') {
      setReportTab('activity');
      if (pid) setActivityProjectId(pid);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [projects, setProjects] = useState([]);
  const [tasks,    setTasks]    = useState([]);
  const [users,    setUsers]    = useState([]);
  const [notes,    setNotes]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [pRes, tRes, uRes, nRes] = await Promise.all([
          getProjects(), getTasks(), getUsers(), getNotes(),
        ]);
        setProjects(pRes.data);
        setTasks(tRes.data);
        setUsers(uRes.data);
        setNotes(nRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const today = new Date().toISOString().split('T')[0];

  const totalProjects     = projects.length;
  const activeProjects    = projects.filter(p => p.meta?.gpf_status === 'active').length;
  const completedProjects = projects.filter(p => p.meta?.gpf_status === 'completed').length;
  const totalTasks        = tasks.length;
  const completedTasks    = tasks.filter(t => t.meta?.gpf_status === 'completed').length;
  const inProgressTasks   = tasks.filter(t => t.meta?.gpf_status === 'in_progress').length;
  const notStartedTasks   = tasks.filter(t => t.meta?.gpf_status === 'not_started').length;
  const overdueTasks      = tasks.filter(t => t.meta?.gpf_end_date && t.meta.gpf_end_date < today && t.meta?.gpf_status !== 'completed').length;
  const totalHours        = notes.reduce((sum, n) => sum + (parseFloat(n.meta?.gpf_hours) || 0), 0);
  const completionRate    = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const taskStatusData = [
    { label: 'Completed',   value: completedTasks,  color: '#16a34a' },
    { label: 'In Progress', value: inProgressTasks, color: '#2563eb' },
    { label: 'Not Started', value: notStartedTasks, color: '#94a3b8' },
    { label: 'Overdue',     value: overdueTasks,    color: '#dc2626' },
  ];

  const overdueList = tasks
    .filter(t => t.meta?.gpf_end_date && t.meta.gpf_end_date < today && t.meta?.gpf_status !== 'completed')
    .sort((a, b) => (a.meta?.gpf_end_date || '').localeCompare(b.meta?.gpf_end_date || ''));

  const hoursPerProject = projects.map(p => {
    const projectNotes = notes.filter(n => n.meta?.gpf_project_id === p.id);
    const hours = projectNotes.reduce((sum, n) => sum + (parseFloat(n.meta?.gpf_hours) || 0), 0);
    return { name: p.title?.rendered || 'Untitled', hours };
  }).filter(p => p.hours > 0).sort((a, b) => b.hours - a.hours);

  const maxHours = hoursPerProject[0]?.hours || 1;

  if (loading) return <Layout><p style={{ color: '#64748b', padding: 32 }}>Loading reports...</p></Layout>;

  return (
    <Layout>
      <div style={styles.page}>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Reports</h1>
            <p style={styles.subtitle}>Overview of your team's activity</p>
          </div>
          <span style={styles.dateLabel}>As of {today}</span>
        </div>

        <div style={{ display: 'flex', gap: 4, background: 'white', padding: 4, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', width: 'fit-content', marginBottom: 20 }}>
          <button
            onClick={() => setReportTab('overview')}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: reportTab === 'overview' ? '#2563eb' : 'transparent', color: reportTab === 'overview' ? 'white' : '#64748b' }}
          >📊 Overview</button>
          <button
            onClick={() => setReportTab('research')}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: reportTab === 'research' ? '#2563eb' : 'transparent', color: reportTab === 'research' ? 'white' : '#64748b' }}
          >🔎 Research Center</button>
          <button
            onClick={() => setReportTab('activity')}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: reportTab === 'activity' ? '#2563eb' : 'transparent', color: reportTab === 'activity' ? 'white' : '#64748b' }}
          >🕐 Activity Timeline</button>
          <button
            onClick={() => setReportTab('deletions')}
            style={{ padding: '8px 18px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: reportTab === 'deletions' ? '#2563eb' : 'transparent', color: reportTab === 'deletions' ? 'white' : '#64748b' }}
          >🗑️ Deletion Log</button>
        </div>

        {reportTab === 'deletions' && (
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <DeletionLog />
          </div>
        )}

        {reportTab === 'research' && <ResearchCenter />}
        {reportTab === 'activity' && (
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Select Project</label>
              <select
                style={{ padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', minWidth: 320 }}
                value={activityProjectId}
                onChange={e => setActivityProjectId(e.target.value)}
              >
                <option value="">Choose a project...</option>
                {activityProjects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.title}{p.is_deleted ? ' (deleted)' : ''}
                  </option>
                ))}
              </select>
              {activityProjects.length === 0 && !loadingActivityProjects && (
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>No project activity recorded yet.</p>
              )}
            </div>
            <ProjectActivityTimeline projectId={activityProjectId} />
          </div>
        )}
        {reportTab === 'overview' && (
        <>
        <div style={styles.statsGrid}>
          {[
            { label: 'Total Projects',  value: totalProjects,              accent: '#2563eb' },
            { label: 'Active Projects', value: activeProjects,             accent: '#0891b2' },
            { label: 'Total Tasks',     value: totalTasks,                 accent: '#7c3aed' },
            { label: 'Completion Rate', value: `${completionRate}%`,       accent: '#16a34a' },
            { label: 'Overdue Tasks',   value: overdueTasks,               accent: '#dc2626' },
            { label: 'Hours Logged',    value: `${totalHours.toFixed(1)}h`,accent: '#d97706' },
            { label: 'Team Members',    value: users.length,               accent: '#0f172a' },
            { label: 'Total Notes',     value: notes.length,               accent: '#64748b' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statCard, borderTop: `3px solid ${s.accent}` }}>
              <span style={{ ...styles.statValue, color: s.accent }}>{s.value}</span>
              <span style={styles.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        <div style={styles.twoCol}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Task Status Breakdown</h2>
            <div style={styles.statusList}>
              {taskStatusData.map(item => (
                <div key={item.label} style={styles.statusRow}>
                  <div style={styles.statusLeft}>
                    <span style={{ ...styles.statusDot, background: item.color }} />
                    <span style={styles.statusLabel}>{item.label}</span>
                  </div>
                  <div style={styles.statusBarWrap}>
                    <div style={styles.statusBarBg}>
                      <div style={{ ...styles.statusBarFill, width: totalTasks > 0 ? `${(item.value / totalTasks) * 100}%` : '0%', background: item.color }} />
                    </div>
                    <span style={styles.statusCount}>{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Project Status</h2>
            <div style={styles.statusList}>
              {[
                { label: 'Active',    value: activeProjects,    color: '#2563eb' },
                { label: 'Completed', value: completedProjects, color: '#16a34a' },
                { label: 'Archived',  value: projects.filter(p => p.meta?.gpf_status === 'archived').length, color: '#94a3b8' },
              ].map(item => (
                <div key={item.label} style={styles.statusRow}>
                  <div style={styles.statusLeft}>
                    <span style={{ ...styles.statusDot, background: item.color }} />
                    <span style={styles.statusLabel}>{item.label}</span>
                  </div>
                  <div style={styles.statusBarWrap}>
                    <div style={styles.statusBarBg}>
                      <div style={{ ...styles.statusBarFill, width: totalProjects > 0 ? `${(item.value / totalProjects) * 100}%` : '0%', background: item.color }} />
                    </div>
                    <span style={styles.statusCount}>{item.value}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.divider} />
            <h2 style={styles.cardTitle}>Overall Completion</h2>
            <div style={styles.completionWrap}>
              <div style={styles.completionBar}>
                <div style={{ ...styles.completionFill, width: `${completionRate}%` }} />
              </div>
              <span style={styles.completionPct}>{completionRate}%</span>
            </div>
          </div>
        </div>

        {hoursPerProject.length > 0 && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Hours Logged by Project</h2>
            <div style={styles.hoursGrid}>
              {hoursPerProject.map(p => (
                <div key={p.name} style={styles.hoursRow}>
                  <span style={styles.hoursName}>{p.name}</span>
                  <div style={styles.hoursBarBg}>
                    <div style={{ ...styles.hoursBarFill, width: `${(p.hours / maxHours) * 100}%` }} />
                  </div>
                  <span style={styles.hoursValue}>{p.hours.toFixed(1)}h</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>
            Overdue Tasks
            {overdueTasks > 0 && <span style={styles.overdueCount}>{overdueTasks}</span>}
          </h2>
          {overdueList.length === 0 ? (
            <p style={styles.emptyText}>🎉 No overdue tasks!</p>
          ) : (
            <div style={styles.overdueList}>
              <div style={styles.overdueHeader}>
                <span style={{ flex: 3 }}>Task</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Priority</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Progress</span>
                <span style={{ flex: 1, textAlign: 'center' }}>Due Date</span>
              </div>
              {overdueList.map(task => {
                const priority = task.meta?.gpf_priority || 'medium';
                const progress = task.meta?.gpf_progress || 0;
                const pc = { high: { bg: '#fef2f2', text: '#dc2626' }, medium: { bg: '#fffbeb', text: '#d97706' }, low: { bg: '#f0fdf4', text: '#16a34a' } }[priority] || { bg: '#fffbeb', text: '#d97706' };
                return (
                  <div key={task.id} style={{ ...styles.overdueRow, cursor: 'pointer' }} onClick={() => navigate(`/projects/${task.meta?.gpf_project_id}/tasks?task=${task.id}`)} title='Click to open task'>
                    <span style={{ flex: 3, fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{task.title?.rendered || 'Untitled'}</span>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <span style={{ ...styles.badge, background: pc.bg, color: pc.text }}>{priority}</span>
                    </div>
                    <span style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#64748b' }}>{progress}%</span>
                    <span style={{ flex: 1, textAlign: 'center', fontSize: 13, color: '#dc2626', fontWeight: 600 }}>{task.meta?.gpf_end_date}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        </>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 1000 },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title:         { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:      { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  dateLabel:     { fontSize: 13, color: '#94a3b8', fontWeight: 500 },
  statsGrid:     { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 },
  statCard:      { background: '#fff', borderRadius: 10, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  statValue:     { fontSize: 28, fontWeight: 700 },
  statLabel:     { fontSize: 12, color: '#64748b', fontWeight: 500 },
  twoCol:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  card:          { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 },
  cardTitle:     { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 18px', display: 'flex', alignItems: 'center', gap: 8 },
  statusList:    { display: 'flex', flexDirection: 'column', gap: 12 },
  statusRow:     { display: 'flex', alignItems: 'center', gap: 12 },
  statusLeft:    { display: 'flex', alignItems: 'center', gap: 8, width: 110, flexShrink: 0 },
  statusDot:     { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  statusLabel:   { fontSize: 13, color: '#374151', fontWeight: 500 },
  statusBarWrap: { flex: 1, display: 'flex', alignItems: 'center', gap: 10 },
  statusBarBg:   { flex: 1, height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  statusBarFill: { height: '100%', borderRadius: 99, transition: 'width 0.4s ease' },
  statusCount:   { fontSize: 13, fontWeight: 700, color: '#0f172a', minWidth: 24, textAlign: 'right' },
  divider:       { borderTop: '1px solid #f1f5f9', margin: '20px 0' },
  completionWrap:{ display: 'flex', alignItems: 'center', gap: 14 },
  completionBar: { flex: 1, height: 12, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  completionFill:{ height: '100%', background: '#16a34a', borderRadius: 99, transition: 'width 0.4s ease' },
  completionPct: { fontSize: 18, fontWeight: 700, color: '#16a34a', minWidth: 48 },
  hoursGrid:     { display: 'flex', flexDirection: 'column', gap: 10 },
  hoursRow:      { display: 'flex', alignItems: 'center', gap: 12 },
  hoursName:     { width: 180, fontSize: 13, fontWeight: 500, color: '#374151', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  hoursBarBg:    { flex: 1, height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' },
  hoursBarFill:  { height: '100%', background: '#d97706', borderRadius: 99 },
  hoursValue:    { fontSize: 13, fontWeight: 700, color: '#d97706', minWidth: 44, textAlign: 'right' },
  overdueList:   { display: 'flex', flexDirection: 'column' },
  overdueHeader: { display: 'flex', padding: '8px 12px', background: '#f8fafc', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  overdueRow:    { display: 'flex', alignItems: 'center', padding: '12px 12px', borderBottom: '1px solid #f8fafc' },
  overdueCount:  { background: '#fef2f2', color: '#dc2626', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99 },
  badge:         { padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, textTransform: 'capitalize', display: 'inline-block' },
  emptyText:     { color: '#64748b', fontSize: 14, margin: 0 },
};